import type { RnbCalculatorPayload } from 'typings'
import { toAPIOriginalValues, toAPIPromotionBase } from '..'
import {
  MoreForLessBenefitVariant,
  MoreForLessConditions,
  MoreForLessEffect,
  MoreForLessPromotion,
} from '../../../../typings/moreForLess'
import {
  toAPIBin,
  toAPICluster,
  toAPIListPrice,
  toAPIOrderValue,
  toAPIPaymentMethods,
  toAPIUtm,
} from '../conditions'
import { toAPIRestrictions } from '../restrictions'

const MORE_FOR_LESS_INCOMPATIBLE_FIELDS: (keyof RnbCalculatorPayload)[] = [
  // Discount section
  'absoluteShippingDiscountValue',
  'nominalDiscountType',
  'nominalShippingDiscountValue',
  'nominalDiscountValue',
  'maximumUnitPriceDiscount',
  'percentualDiscountValue',
  'rebatePercentualDiscountValue',
  'percentualShippingDiscountValue',
  'percentualTax',
  'shippingPercentualTax',
  'taxExpression',
  'discountExpression',
  'percentualDiscountValueList1',
  'percentualDiscountValueList2',
  'activateGiftsMultiplier',
  'nominalRewardValue',
  'percentualRewardValue',
  'orderStatusRewardValue',
  'nominalTax',
  'priceTableName',
  'percentualDiscountValueList',

  // Buy and win section
  'skusGift',
  'collections1BuyTogether',
  'collections2BuyTogether',
  'minimumQuantityBuyTogether',
  'quantityToAffectBuyTogether',
  'enableBuyTogetherPerSku',
  'listSku1BuyTogether',
  'listSku2BuyTogether',
]

type APIMoreForLessConditions = ReturnType<
  typeof toAPIPaymentMethods &
    typeof toAPIBin &
    typeof toAPIOrderValue &
    typeof toAPIUtm &
    typeof toAPICluster &
    typeof toAPIListPrice
>
type APIMoreForLessSectionPayload = Pick<
  Partial<RnbCalculatorPayload>,
  | 'listSku1BuyTogether'
  | 'minimumQuantityBuyTogether'
  | 'collections1BuyTogether'
  | 'quantityToAffectBuyTogether'
  | 'percentualDiscountValue'
  | 'maximumUnitPriceDiscount'
  | 'enableBuyTogetherPerSku'
  | 'isAppliedToMostExpensive'
  | 'shouldDistributeDiscountAmongMatchedItems'
>

export function toAPIMoreForLessEffect(
  config: MoreForLessEffect
): APIMoreForLessSectionPayload {
  const { trigger, benefit } = config
  if (!trigger || !benefit)
    throw new Error(
      'Cannot convert Promotion to API Payload: Invalid More For Less Effect',
      { cause: { payload: config } }
    )

  return {
    listSku1BuyTogether: trigger.skus,
    minimumQuantityBuyTogether: trigger.minQuantity,
    collections1BuyTogether: trigger.collections,
    quantityToAffectBuyTogether: benefit.quantity,
    percentualDiscountValue:
      benefit.discount.type === MoreForLessBenefitVariant.PERCENTUAL
        ? benefit.discount.value
        : 0,
    maximumUnitPriceDiscount:
      benefit.discount.type === MoreForLessBenefitVariant.MAX_PRICE_PER_ITEM
        ? benefit.discount.value
        : 0,
    enableBuyTogetherPerSku: benefit.apply.equals,
    isAppliedToMostExpensive: benefit.apply.mostExpensive,
    shouldDistributeDiscountAmongMatchedItems: benefit.apply.distributed,
  }
}

export function toAPIMoreForLessConditions(
  conditions: MoreForLessConditions
): APIMoreForLessConditions {
  return {
    ...toAPIPaymentMethods(conditions.paymentMethods),
    ...toAPIBin(conditions.bin),
    ...toAPIOrderValue(conditions.orderValue),
    ...toAPIUtm(conditions.utm),
    ...toAPICluster(conditions.cluster),
    ...toAPIListPrice(conditions.listPrice),
  }
}

type APIMoreForLessPayload = ReturnType<
  typeof toAPIOriginalValues &
    typeof toAPIMoreForLessEffect &
    typeof toAPIMoreForLessConditions &
    typeof toAPIRestrictions &
    typeof toAPIPromotionBase
>

export function toAPIMoreForLess(
  data: MoreForLessPromotion,
  originalValues?: RnbCalculatorPayload
): APIMoreForLessPayload {
  return {
    ...toAPIOriginalValues(originalValues, MORE_FOR_LESS_INCOMPATIBLE_FIELDS),
    ...toAPIPromotionBase(data),
    ...toAPIMoreForLessEffect(data.moreForLess),
    ...toAPIMoreForLessConditions(data.conditions),
    ...toAPIRestrictions(data.restrictions),
  }
}
